import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, Phone, FileText, Home } from 'lucide-react';
import api from '../api/apiClient';
import { useAuth } from '../contexts/AuthContext';

interface OnboardingStatus {
  emailVerified: boolean;
  phoneVerified: boolean;
  idUploaded: boolean;
  leaseConnected: boolean;
  paymentMethodAdded: boolean;
  onboardingCompleted: boolean;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  // Email verification
  const [emailToken, setEmailToken] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  // Phone verification
  const [phone, setPhone] = useState('');
  const [phoneToken, setPhoneToken] = useState('');
  const [sendingPhone, setSendingPhone] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);

  // Lease connection
  const [availableLeases, setAvailableLeases] = useState<any[]>([]);
  const [selectedLeaseId, setSelectedLeaseId] = useState<number | null>(null);
  const [connectingLease, setConnectingLease] = useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (status) {
      determineCurrentStep();
    }
  }, [status]);

  const loadStatus = async () => {
    try {
      const response = await api.get<OnboardingStatus>('/renter/onboarding/status');
      setStatus(response);

      // If onboarding is complete, redirect to dashboard
      if (response.onboardingCompleted) {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  };

  const determineCurrentStep = () => {
    if (!status) return;
    if (!status.emailVerified) setCurrentStep(0);
    else if (!status.phoneVerified) setCurrentStep(1);
    else if (!status.idUploaded) setCurrentStep(2);
    else if (!status.leaseConnected) setCurrentStep(3);
    else setCurrentStep(4);
  };

  const sendEmailVerification = async () => {
    setSendingEmail(true);
    setError('');
    try {
      await api.post('/renter/onboarding/send-email-token', {});
      alert('Verification code sent to your email!');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSendingEmail(false);
    }
  };

  const verifyEmail = async () => {
    setVerifyingEmail(true);
    setError('');
    try {
      await api.post('/renter/onboarding/verify-email', { token: emailToken });
      await loadStatus();
      await refreshUser();
    } catch (err: any) {
      setError('Invalid verification code');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const sendPhoneVerification = async () => {
    if (!phone) {
      setError('Please enter your phone number');
      return;
    }
    setSendingPhone(true);
    setError('');
    try {
      await api.post('/renter/onboarding/send-phone-token', { phone });
      alert('Verification code sent to your phone!');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSendingPhone(false);
    }
  };

  const verifyPhone = async () => {
    setVerifyingPhone(true);
    setError('');
    try {
      await api.post('/renter/onboarding/verify-phone', { phone, token: phoneToken });
      await loadStatus();
      await refreshUser();
    } catch (err: any) {
      setError('Invalid verification code');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const skipIdUpload = async () => {
    setCurrentStep(3);
  };

  const loadLeases = async () => {
    try {
      const response = await api.get<{leases: any[]}>('/renter/onboarding/available-leases');
      setAvailableLeases(response.leases || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load leases');
    }
  };

  const connectLease = async () => {
    if (!selectedLeaseId) {
      setError('Please select a lease');
      return;
    }
    setConnectingLease(true);
    setError('');
    try {
      await api.post('/renter/onboarding/connect-lease', { leaseId: selectedLeaseId });
      await loadStatus();
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to connect lease');
    } finally {
      setConnectingLease(false);
    }
  };

  useEffect(() => {
    if (currentStep === 3) {
      loadLeases();
    }
  }, [currentStep]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#0C4432' }}></div>
          <p className="mt-4" style={{ color: '#222222' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const steps = [
    { label: 'Verify Email', icon: Mail, completed: status?.emailVerified },
    { label: 'Verify Phone', icon: Phone, completed: status?.phoneVerified },
    { label: 'Upload ID', icon: FileText, completed: status?.idUploaded },
    { label: 'Connect Lease', icon: Home, completed: status?.leaseConnected }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold mb-2" style={{ color: '#0C4432' }}>
            Welcome to Rentry!
          </h1>
          <p style={{ color: '#222222' }}>Let's get your account set up</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12">
          {steps.map((step, idx) => (
            <div key={idx} className="flex flex-col items-center flex-1">
              <div className={`rounded-full p-2 ${step.completed ? 'bg-green-500' : idx === currentStep ? 'bg-blue-500' : 'bg-gray-300'}`}>
                {step.completed ? (
                  <CheckCircle className="w-6 h-6 text-white" />
                ) : (
                  <step.icon className="w-6 h-6 text-white" />
                )}
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: '#222222' }}>{step.label}</p>
            </div>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#DF6C4F', color: '#F8F8F8' }}>
            {error}
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* Email Verification */}
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Verify Your Email</h2>
              <p className="mb-4" style={{ color: '#222222' }}>
                We'll send a verification code to {user?.email}
              </p>
              <button
                onClick={sendEmailVerification}
                disabled={sendingEmail}
                className="w-full mb-4 py-2 px-4 rounded-md text-white font-medium disabled:opacity-50"
                style={{ backgroundColor: '#0C4432' }}
              >
                {sendingEmail ? 'Sending...' : 'Send Verification Code'}
              </button>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={emailToken}
                onChange={(e) => setEmailToken(e.target.value)}
                className="w-full mb-4 px-3 py-2 border rounded-md"
                style={{ borderColor: '#49c5b6' }}
              />
              <button
                onClick={verifyEmail}
                disabled={verifyingEmail || !emailToken}
                className="w-full py-2 px-4 rounded-md text-white font-medium disabled:opacity-50"
                style={{ backgroundColor: '#49c5b6' }}
              >
                {verifyingEmail ? 'Verifying...' : 'Verify Email'}
              </button>
            </div>
          )}

          {/* Phone Verification */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Verify Your Phone</h2>
              <p className="mb-4" style={{ color: '#222222' }}>
                Add your phone number for important notifications
              </p>
              <input
                type="tel"
                placeholder="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full mb-4 px-3 py-2 border rounded-md"
                style={{ borderColor: '#49c5b6' }}
              />
              <button
                onClick={sendPhoneVerification}
                disabled={sendingPhone || !phone}
                className="w-full mb-4 py-2 px-4 rounded-md text-white font-medium disabled:opacity-50"
                style={{ backgroundColor: '#0C4432' }}
              >
                {sendingPhone ? 'Sending...' : 'Send Verification Code'}
              </button>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={phoneToken}
                onChange={(e) => setPhoneToken(e.target.value)}
                className="w-full mb-4 px-3 py-2 border rounded-md"
                style={{ borderColor: '#49c5b6' }}
              />
              <button
                onClick={verifyPhone}
                disabled={verifyingPhone || !phoneToken}
                className="w-full py-2 px-4 rounded-md text-white font-medium disabled:opacity-50 mb-2"
                style={{ backgroundColor: '#49c5b6' }}
              >
                {verifyingPhone ? 'Verifying...' : 'Verify Phone'}
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                className="w-full py-2 px-4 rounded-md font-medium"
                style={{ color: '#49c5b6', borderColor: '#49c5b6', border: '1px solid' }}
              >
                Skip for now
              </button>
            </div>
          )}

          {/* ID Upload */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Upload ID (Optional)</h2>
              <p className="mb-4" style={{ color: '#222222' }}>
                Upload a photo ID to verify your identity. This can be done later.
              </p>
              <button
                onClick={skipIdUpload}
                className="w-full py-2 px-4 rounded-md text-white font-medium"
                style={{ backgroundColor: '#49c5b6' }}
              >
                Skip for now
              </button>
            </div>
          )}

          {/* Lease Connection */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Connect Your Lease</h2>
              {availableLeases.length === 0 ? (
                <div>
                  <p className="mb-4" style={{ color: '#222222' }}>
                    No leases found. Your agent will assign a lease to your account soon.
                  </p>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full py-2 px-4 rounded-md text-white font-medium"
                    style={{ backgroundColor: '#0C4432' }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : (
                <div>
                  <p className="mb-4" style={{ color: '#222222' }}>Select your lease:</p>
                  <div className="space-y-3 mb-4">
                    {availableLeases.map((lease) => (
                      <div
                        key={lease.id}
                        onClick={() => setSelectedLeaseId(lease.id)}
                        className={`p-4 border-2 rounded-md cursor-pointer ${selectedLeaseId === lease.id ? 'border-blue-500' : 'border-gray-300'}`}
                      >
                        <p className="font-semibold">{lease.propertyAddress}</p>
                        <p className="text-sm" style={{ color: '#666' }}>
                          ${lease.rentAmount} / {lease.frequency}
                        </p>
                        <p className="text-xs" style={{ color: '#999' }}>
                          {lease.startDate} - {lease.endDate || 'Ongoing'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={connectLease}
                    disabled={connectingLease || !selectedLeaseId}
                    className="w-full py-2 px-4 rounded-md text-white font-medium disabled:opacity-50"
                    style={{ backgroundColor: '#0C4432' }}
                  >
                    {connectingLease ? 'Connecting...' : 'Connect Lease'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

